import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Loader2, Trash2, Bell, Upload, Download, FileSpreadsheet, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import BulkActionBar from "@/components/BulkActionBar";
import ClientFormDialog from "@/components/ClientFormDialog";
import { atLeast, useAuth } from "@/lib/auth";

export default function ClientsPage() {
  const { user } = useAuth();
  const canBulkDelete = atLeast(user, "partner");
  const canEdit = atLeast(user, "article_assistant");
  const canRemind = atLeast(user, "manager");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [outstanding, setOutstanding] = useState({});
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(searchParams.get("new") === "1");
  const [selected, setSelected] = useState(new Set());
  const [remindingId, setRemindingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchClients = useCallback(() => {
    api.get("/clients", { params: q ? { q } : {} }).then(({ data }) => {
      setClients(data);
      setSelected(new Set());
    });
    api.get("/clients/-/outstanding-summary").then(({ data }) => setOutstanding(data)).catch(() => {});
  }, [q]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const sendReminder = async (e, c) => {
    e.stopPropagation();
    if (!c.email) {
      toast.error("Client has no email on file");
      return;
    }
    const o = outstanding[c.id];
    if (!o || o.outstanding <= 0) {
      toast.error("No outstanding balance for this client");
      return;
    }
    setRemindingId(c.id);
    try {
      const { data } = await api.post(`/clients/${c.id}/send-payment-reminder`);
      toast.success(`Reminder sent to ${c.email} — ₹${data.outstanding.toLocaleString("en-IN")}`);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to send reminder"));
    } finally {
      setRemindingId(null);
    }
  };

  const openNew = () => {
    setNewOpen(true);
    setSearchParams({ new: "1" });
  };

  const closeNew = () => {
    setNewOpen(false);
    searchParams.delete("new");
    setSearchParams(searchParams);
  };

  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (!clients) return;
    setSelected((s) => (s.size === clients.length ? new Set() : new Set(clients.map((c) => c.id))));
  };
  const bulkDelete = async () => {
    const reason = window.prompt(`Archive ${selected.size} client(s)? Reason (min 5 chars, required for audit):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      const { data } = await api.post(
        "/bulk/clients/delete",
        { ids: Array.from(selected) },
        { headers: { "X-Change-Reason": reason.trim() } },
      );
      toast.success(`Archived ${data.updated} client(s)`);
      fetchClients();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk delete failed"));
    }
  };

  const archiveSingle = async (e, c) => {
    e.stopPropagation();
    const reason = window.prompt(`Archive client "${c.name}"? Reason (min 5 chars, required for audit):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.delete(`/clients/${c.id}`, { headers: { "X-Change-Reason": reason.trim() } });
      toast.success("Client archived");
      fetchClients();
    } catch (err) {
      toast.error(errorMessage(err, "Archive failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search clients, PAN, GSTIN..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="clients-search"
          />
        </div>
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground font-mono">
          {clients?.length ?? "—"} clients
        </div>
        <Button onClick={() => setImportOpen(true)} variant="outline" data-testid="import-clients-btn" size="sm">
          <FileSpreadsheet size={13} className="mr-1.5" /> Import Excel
        </Button>
        <Button onClick={openNew} data-testid="new-client-btn" size="sm">
          <Plus size={13} className="mr-1.5" /> New client
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              {canBulkDelete && (
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={clients && clients.length > 0 && selected.size === clients.length}
                    onCheckedChange={toggleAll}
                    data-testid="clients-select-all"
                    aria-label="Select all clients"
                  />
                </th>
              )}
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Code</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Name</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Constitution</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">PAN</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">GSTIN</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Mobile</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Services</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Outstanding</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right w-8"></th>
            </tr>
          </thead>
          <tbody>
            {clients === null && (
              <tr><td colSpan={canBulkDelete ? 9 : 8} className="px-3 py-6 text-center text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin inline mr-2" />Loading...</td></tr>
            )}
            {clients?.length === 0 && (
              <tr><td colSpan={canBulkDelete ? 9 : 8} className="px-3 py-10 text-center text-sm text-muted-foreground">
                No clients yet. <button onClick={openNew} className="underline">Create your first client</button>.
              </td></tr>
            )}
            {clients?.map((c) => {
              const isSelected = selected.has(c.id);
              const o = outstanding[c.id];
              const hasBalance = o && o.outstanding > 0;
              return (
              <tr
                key={c.id}
                onClick={(e) => {
                  if (e.target.closest("[data-row-checkbox]") || e.target.closest("[data-row-action]")) return;
                  navigate(`/clients/${c.id}`);
                }}
                data-testid={`client-row-${c.client_code}`}
                className={`border-b border-border hover:bg-muted/40 cursor-pointer row-hover ${isSelected ? "bg-primary/5" : ""}`}
              >
                {canBulkDelete && (
                  <td className="px-3 py-2.5" data-row-checkbox>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(c.id)}
                      data-testid={`client-select-${c.client_code}`}
                      aria-label={`Select ${c.name}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2.5 font-mono text-xs">{c.client_code}</td>
                <td className="px-3 py-2.5 font-medium">{c.name}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">{c.constitution.replace(/_/g, " ")}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{c.pan || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{c.gstin || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{c.mobile || c.phone || "—"}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(c.services_availed || []).slice(0, 3).map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px] font-normal">{s}</Badge>
                    ))}
                    {(c.services_availed || []).length > 3 && (
                      <span className="text-[10px] text-muted-foreground">+{c.services_availed.length - 3}</span>
                    )}
                  </div>
                </td>
                <td className={`px-3 py-2.5 text-right font-mono text-xs ${hasBalance ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}`} data-testid={`client-outstanding-${c.client_code}`}>
                  {o ? `₹${o.outstanding.toLocaleString("en-IN", { minimumFractionDigits: 0 })}` : "—"}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap" data-row-action>
                  {canEdit && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                      title="Edit client"
                      data-testid={`client-edit-${c.client_code}`}
                    >
                      <Pencil size={12} />
                    </Button>
                  )}
                  {canRemind && hasBalance && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-brand-gold hover:text-amber-700 hover:bg-amber-500/10"
                      onClick={(e) => sendReminder(e, c)}
                      disabled={remindingId === c.id || !c.email}
                      title={c.email ? "Send payment reminder" : "Client has no email"}
                      data-testid={`client-remind-${c.client_code}`}
                    >
                      {remindingId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                    </Button>
                  )}
                  {canBulkDelete && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      onClick={(e) => archiveSingle(e, c)}
                      title="Archive client"
                      data-testid={`client-archive-${c.client_code}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create and edit share one dialog — passing a client switches it to
          edit mode; the fields, validation and layout stay in one place. */}
      <ClientFormDialog
        open={newOpen}
        onOpenChange={(v) => (v ? openNew() : closeNew())}
        onSaved={fetchClients}
      />

      <BulkActionBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        actions={canBulkDelete ? [{
          label: "Archive",
          icon: <Trash2 size={13} />,
          onClick: bulkDelete,
          variant: "destructive",
          testid: "bulk-archive-clients",
        }] : []}
      />

      <ImportClientsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={fetchClients}
      />

      <ClientFormDialog
        client={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={fetchClients}
      />
    </div>
  );
}


function ImportClientsDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dryRun, setDryRun] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/clients/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ca-os-clients-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Choose an .xlsx file first");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", String(dryRun));
      const { data } = await api.post("/clients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (!dryRun && data.created_count > 0) {
        toast.success(`Imported ${data.created_count} client(s)`);
        onImported?.();
      } else if (dryRun) {
        toast.success(`Dry-run: ${data.created_count} would be created, ${data.skipped_count} skipped, ${data.error_count} errors`);
      } else if (data.created_count === 0) {
        toast.info(`Nothing imported — ${data.skipped_count} duplicates, ${data.error_count} errors`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); setDryRun(false); };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
    >
      <DialogContent className="max-w-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-primary" />
            Import clients from Excel
          </DialogTitle>
          <DialogDescription>
            Bulk-create clients from an .xlsx spreadsheet. Sub-categories (services, credentials) can be filled per-client after import.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 flex items-center gap-3">
              <div className="text-sm flex-1">
                <div className="font-medium">Step 1 · Download the template</div>
                <div className="text-xs text-muted-foreground">Column headers + a sample row are pre-filled.</div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="import-download-template">
                <Download size={13} className="mr-1.5" /> Download
              </Button>
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="text-sm font-medium">Step 2 · Upload the filled file</div>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md py-8 cursor-pointer hover:bg-muted/40 transition-colors" data-testid="import-drop-zone">
                <Upload size={20} className="text-muted-foreground mb-2" />
                <div className="text-sm">
                  {file ? <span className="font-mono">{file.name}</span> : "Click to choose an .xlsx file"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">.xlsx up to a few thousand rows</div>
                <input
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="import-file-input"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className="accent-primary"
                  data-testid="import-dry-run"
                />
                Dry-run first (preview without creating)
              </label>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3 text-center">
                <div className="text-2xl font-heading font-semibold text-emerald-600 dark:text-emerald-400">{result.created_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{result.dry_run ? "Would create" : "Created"}</div>
              </div>
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-3 text-center">
                <div className="text-2xl font-heading font-semibold text-amber-600 dark:text-amber-400">{result.skipped_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Skipped</div>
              </div>
              <div className="border border-red-500/30 bg-red-500/5 rounded-md p-3 text-center">
                <div className="text-2xl font-heading font-semibold text-red-600 dark:text-red-400">{result.error_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Errors</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-border rounded-md max-h-40 overflow-y-auto">
                <div className="text-xs uppercase tracking-widest text-muted-foreground px-3 py-2 border-b border-border">Errors</div>
                {result.errors.map((e) => (
                  <div key={`err-${e.row}-${e.code || e.reason}`} className="px-3 py-1.5 text-xs border-b border-border last:border-0 flex gap-3">
                    <span className="font-mono text-muted-foreground w-14">Row {e.row}</span>
                    <span className="text-red-600 dark:text-red-400">{e.reason}</span>
                    {e.code && <span className="ml-auto font-mono text-muted-foreground">{e.code}</span>}
                  </div>
                ))}
              </div>
            )}
            {result.skipped?.length > 0 && (
              <div className="border border-border rounded-md max-h-40 overflow-y-auto">
                <div className="text-xs uppercase tracking-widest text-muted-foreground px-3 py-2 border-b border-border">Skipped (duplicates)</div>
                {result.skipped.map((s) => (
                  <div key={`skip-${s.row}-${s.code}`} className="px-3 py-1.5 text-xs border-b border-border last:border-0 flex gap-3">
                    <span className="font-mono text-muted-foreground w-14">Row {s.row}</span>
                    <span className="font-mono">{s.code}</span>
                    <span className="text-muted-foreground">{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="ghost" onClick={() => setResult(null)}>Import another</Button>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !file} data-testid="import-submit">
                {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
                {dryRun ? "Preview import" : "Import clients"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
